import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import CitySelector from "../../components/dropdowns/CitySelector";
import CreateCompanyModal from "./Modals/CreateCompanyModal";
import { useAuth } from "../../contexts/AuthContext";
import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilIcon,
  CheckCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import EditCompanyModal from "./Modals/EditCompanyModal";
import { ShieldCheckIcon } from "@heroicons/react/24/outline";
import Button from "../../components/controls/Button";
import FormInput from "../../components/controls/FormInput";
import DataGrid from "../../components/Datagrid";

export default function RepresentativeCustomersReservation() {
  const { t } = useTranslation();
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [myReservations, setMyReservations] = useState([]);
  const [cityList, setCityList] = useState([]);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [companyToEdit, setCompanyToEdit] = useState(null);

  const [currentPageCompanies, setCurrentPageCompanies] = useState(1);
  const itemsPerPageCompanies = 10;

  const [currentPageReservations, setCurrentPageReservations] = useState(1);
  const itemsPerPageReservations = 10;

  useEffect(() => {
    const fetchCompanies = async () => {
      if (!selectedCityId) return;
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/Representative/CompaniesToContact?cityId=${selectedCityId}`
        );
        const data = await res.json();
        setCompanies(data);
        setCurrentPageCompanies(1);
      } catch (err) {
        console.error("Erreur chargement compagnies", err);
      }
    };
    fetchCompanies();
  }, [selectedCityId]);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const res = await fetch("/geojson/simplified_cities.json");
        const data = await res.json();
        const sorted = data
          .filter((c) => c.nameFr || c.nameEn)
          .sort((a, b) =>
            (a.nameFr || a.nameEn).localeCompare(b.nameFr || b.nameEn)
          );
        setCityList(sorted);
      } catch (err) {
        console.error("Erreur chargement des villes", err);
      }
    };
    fetchCities();
  }, []);

  const getCityNameById = (id) => {
    const city = cityList.find((c) => c.id === id);
    return city?.nameFr || city?.nameEn || "-";
  };

  const handleEdit = (company) => {
    setCompanyToEdit(company);
    setIsEditModalOpen(true);
  };

  const handleCompanyUpdated = (updatedCompany) => {
    setCompanies((prev) =>
      prev.map((c) => (c.id === updatedCompany.id ? updatedCompany : c))
    );
    setMyReservations((prev) =>
      prev.map((c) => (c.id === updatedCompany.id ? updatedCompany : c))
    );
  };

  const handleUnassign = async (company) => {
    const confirm = window.confirm(
      `Retirer la réservation pour "${company.name}" ?`
    );
    if (!confirm) return;

    try {
      const payload = {
        name: company.name,
        cityId: company.cityId,
        createdBy: company.createdBy,
        assignedTo: null,
        note: company.note,
      };
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Representative/CompaniesToContact/${company.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error("Erreur lors du retrait");

      setCompanies((prev) =>
        prev.map((c) => (c.id === company.id ? { ...c, assignedTo: null } : c))
      );
      setMyReservations((prev) => prev.filter((c) => c.id !== company.id));
    } catch (err) {
      alert(err.message);
    }
  };

  useEffect(() => {
    const fetchMyReservations = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/Representative/CompaniesToContact/MyReservations?userId=${currentUser?.id}`
        );
        const data = await res.json();
        setMyReservations(data);
      } catch (err) {
        console.error("Erreur chargement de mes réservations", err);
      }
    };
    if (currentUser?.id) fetchMyReservations();
  }, [currentUser?.id]);

  const markAsDone = async (id) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Representative/CompaniesToContact/${id}/complete`,
        { method: "PUT" }
      );
      if (!res.ok) throw new Error("Erreur lors du marquage comme fait");
      setMyReservations((prev) => prev.filter((c) => c.id !== id));
      setCompanies((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      alert(err.message);
    }
  };

  const handleReserve = async (company) => {
    const confirm = window.confirm(
      `${t("reservation.confirmReserve")} "${company.name}" ?`
    );
    if (!confirm) return;

    try {
      const payload = {
        name: company.name,
        cityId: company.cityId,
        createdBy: company.createdBy,
        assignedTo: currentUser?.id,
        note: company.note,
      };
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Representative/CompaniesToContact/${company.id}/reserve`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error("Erreur lors de la réservation");

      setCompanies((prev) =>
        prev.map((c) =>
          c.id === company.id ? { ...c, assignedTo: currentUser?.id } : c
        )
      );

      setMyReservations((prev) => [
        ...prev,
        { ...company, assignedTo: currentUser?.id },
      ]);
    } catch (err) {
      alert(err.message);
    }
  };

  const filteredCompanies = useMemo(
    () =>
      companies.filter((company) =>
        (company.name || "").toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [companies, searchTerm]
  );

  const totalPagesCompanies = Math.ceil(
    filteredCompanies.length / itemsPerPageCompanies
  );

  const paginatedCompanies = useMemo(
    () =>
      filteredCompanies.slice(
        (currentPageCompanies - 1) * itemsPerPageCompanies,
        currentPageCompanies * itemsPerPageCompanies
      ),
    [filteredCompanies, currentPageCompanies]
  );

  const totalPagesReservations = Math.ceil(
    myReservations.length / itemsPerPageReservations
  );

  const paginatedReservations = useMemo(
    () =>
      myReservations.slice(
        (currentPageReservations - 1) * itemsPerPageReservations,
        currentPageReservations * itemsPerPageReservations
      ),
    [myReservations, currentPageReservations]
  );

  useEffect(() => {
    setCurrentPageCompanies(1);
  }, [searchTerm]);

  const companiesColumns = useMemo(
    () => [
      {
        key: "name",
        label: t("reservation.companyName"),
        format: (v) => <span className="font-medium">{v}</span>,
      },
      {
        key: "note",
        label: t("global.note"),
        format: (v) => (v ? v : <span className="text-gray-400">-</span>),
        cellClassName: "max-w-xs",
      },
      {
        key: "assignedTo",
        label: t("reservation.isAssigned"),
        format: (v) => (v ? "Réservé" : "-"),
      },
      {
        key: "actions",
        label: "",
        width: 220,
        render: (row) => (
          <div className="px-4 py-4">
            <div className="flex gap-2 justify-end">
              {!row.assignedTo && (
                <Button
                  type="button"
                  color="primary"
                  size="sm"
                  onClick={() => handleReserve(row)}
                >
                  {t("reservation.reserve")}
                </Button>
              )}
              <Button
                type="button"
                color="secondary"
                size="sm"
                className="shadow-lg"
                onClick={() => handleEdit(row)}
              >
                <PencilIcon className="w-5 h-5" />
              </Button>
            </div>
          </div>
        ),
      },
    ],
    [t, currentUser?.id, companies, searchTerm]
  );

  const reservationsColumns = useMemo(
    () => [
      {
        key: "name",
        label: t("reservation.companyName"),
        format: (v) => <span className="font-medium">{v}</span>,
      },
      {
        key: "note",
        label: t("global.note"),
        format: (v) => (v ? v : <span className="text-gray-400">-</span>),
        cellClassName: "max-w-xs",
      },
      {
        key: "cityId",
        label: t("global.city"),
        format: (v, row) => getCityNameById(row.cityId),
      },
      {
        key: "actions",
        label: "",
        width: 260,
        render: (row) => (
          <div className="px-4 py-4">
            <div className="flex gap-2 justify-end">
              {row.isDone ? (
                <span className="text-xs italic flex items-center gap-2">
                  {t("reservation.completed")}
                  <ShieldCheckIcon className="w-4 h-4" />
                </span>
              ) : (
                <>
                  <Button
                    type="button"
                    color="primary"
                    size="sm"
                    onClick={() => markAsDone(row.id)}
                    leftIcon={<CheckCircleIcon className="w-5 h-5" />}
                  >
                    {t("reservation.markDone")}
                  </Button>

                  <Button
                    type="button"
                    color="secondary"
                    size="sm"
                    className="shadow-lg"
                    onClick={() => handleEdit(row)}
                  >
                    <PencilIcon className="w-5 h-5" />
                  </Button>

                  <Button
                    type="button"
                    color="secondary"
                    className="shadow-lg"
                    size="sm"
                    onClick={() => handleUnassign(row)}
                  >
                    <TrashIcon className="w-5 h-5 text-red-500" />
                  </Button>
                </>
              )}
            </div>
          </div>
        ),
      },
    ],
    [t, cityList, myReservations]
  );

  return (
    <div className="p-6 text-primary">
      <Button
        type="button"
        color="primary"
        size="sm"
        onClick={() => navigate(-1)}
        leftIcon={<ArrowLeftIcon className="w-5 h-5" />}
        className="m-2"
      >
        {t("global.back")}
      </Button>

      <div className="flex flex-col md:flex-row gap-6 h-[calc(100vh-48px)]">
        <div className="w-full md:w-1/2 space-y-4 flex flex-col">
          <div className="flex flex-col md:flex-row items-end justify-between gap-4">
            <CitySelector value={selectedCityId} onChange={setSelectedCityId} />

            <div className="flex items-end gap-2 w-full md:w-auto">
              <div className="w-full md:w-72">
                <FormInput
                  label=""
                  value={searchTerm}
                  showRequiredOrOptional={false}
                  placeholder={t("global.search")}
                  onChange={(name, value) => setSearchTerm(value)}
                />
              </div>

              <Button
                type="button"
                color="primary"
                size="sm"
                onClick={() => setIsModalOpen(true)}
                className="whitespace-nowrap"
              >
                + {t("global.add")}
              </Button>
            </div>
          </div>

          {filteredCompanies.length === 0 ? (
            <div className="text-center text-gray-500 mt-4">
              {t("reservation.noCompanies")}
            </div>
          ) : (
            <>
              <div className="flex-1">
                <DataGrid
                  columns={companiesColumns}
                  data={paginatedCompanies}
                  emptyMessage={t("reservation.noCompanies")}
                />
              </div>

              {totalPagesCompanies > 1 && (
                <div className="flex items-center justify-between px-4 py-2 bg-base-100 border-t rounded-lg">
                  <div className="text-sm text-gray-600">
                    {(currentPageCompanies - 1) * itemsPerPageCompanies + 1} -{" "}
                    {Math.min(
                      currentPageCompanies * itemsPerPageCompanies,
                      filteredCompanies.length
                    )}{" "}
                    sur {filteredCompanies.length}
                  </div>

                  <div className="flex gap-2 items-center">
                    <Button
                      type="button"
                      color="secondary"
                      size="sm"
                      onClick={() =>
                        setCurrentPageCompanies((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPageCompanies === 1}
                      leftIcon={<ChevronLeftIcon className="w-4 h-4" />}
                    >
                      {t("global.previous") || "Précédent"}
                    </Button>

                    <span className="flex items-center px-2">
                      Page {currentPageCompanies} / {totalPagesCompanies}
                    </span>

                    <Button
                      type="button"
                      color="secondary"
                      size="sm"
                      onClick={() =>
                        setCurrentPageCompanies((prev) =>
                          Math.min(totalPagesCompanies, prev + 1)
                        )
                      }
                      disabled={currentPageCompanies === totalPagesCompanies}
                      leftIcon={<ChevronRightIcon className="w-4 h-4" />}
                    >
                      {t("global.next") || "Suivant"}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="hidden md:block w-px bg-gray-300" />

        <div className="w-full md:w-1/2 space-y-4 flex flex-col">
          <h2 className="text-lg font-semibold">
            {t("reservation.myReservations")}
          </h2>

          {myReservations.length === 0 ? (
            <div className="text-gray-500">{t("reservation.none")}</div>
          ) : (
            <>
              <div className="flex-1">
                <DataGrid
                  columns={reservationsColumns}
                  data={paginatedReservations}
                  emptyMessage={t("reservation.none")}
                />
              </div>

              {totalPagesReservations > 1 && (
                <div className="flex items-center justify-between px-4 py-2 bg-base-100 border-t rounded-lg">
                  <div className="text-sm text-gray-600">
                    {(currentPageReservations - 1) * itemsPerPageReservations +
                      1}{" "}
                    -{" "}
                    {Math.min(
                      currentPageReservations * itemsPerPageReservations,
                      myReservations.length
                    )}{" "}
                    sur {myReservations.length}
                  </div>

                  <div className="flex gap-2 items-center">
                    <Button
                      type="button"
                      color="secondary"
                      size="sm"
                      onClick={() =>
                        setCurrentPageReservations((prev) =>
                          Math.max(1, prev - 1)
                        )
                      }
                      disabled={currentPageReservations === 1}
                      leftIcon={<ChevronLeftIcon className="w-4 h-4" />}
                    >
                      {t("global.previous") || "Précédent"}
                    </Button>

                    <span className="flex items-center px-2">
                      Page {currentPageReservations} / {totalPagesReservations}
                    </span>

                    <Button
                      type="button"
                      color="secondary"
                      size="sm"
                      onClick={() =>
                        setCurrentPageReservations((prev) =>
                          Math.min(totalPagesReservations, prev + 1)
                        )
                      }
                      disabled={
                        currentPageReservations === totalPagesReservations
                      }
                      leftIcon={<ChevronRightIcon className="w-4 h-4" />}
                    >
                      {t("global.next") || "Suivant"}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <CreateCompanyModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        cityId={selectedCityId}
        cityIdChanged={setSelectedCityId}
        onCompanyCreated={(company) => {
          setCompanies((prev) => [company, ...prev]);
          if (company.assignedTo === currentUser?.id) {
            setMyReservations((prev) => [company, ...prev]);
          }
        }}
      />

      <EditCompanyModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setCompanyToEdit(null);
        }}
        company={companyToEdit}
        onCompanyUpdated={handleCompanyUpdated}
      />
    </div>
  );
}
